import type { MaybeRefOrGetter } from 'vue';
import type { AnyItem, CompositePrimaryKey, RelationHandlers, ResolvedMonospaceSingleRelationField } from '#layers/data-model/public';
import { reactiveComputed } from '@vueuse/core';
import { computed, toValue } from 'vue';
import { noop } from '#layers/base/public';
import { getRelationConstraint } from '#layers/data-model/public';
import { createRelationFieldStateToManyHandler } from '#layers/forms/utils/relation-to-many-handler';
import { createRelationFieldStateToOneInCreateHandler } from '#layers/forms/utils/relation-to-one-create-handler';
import { createRelationFieldStateToOneInUpdateHandler } from '#layers/forms/utils/relation-to-one-update-handler';
import { injectFormBlockContext } from './form-context';
import { toReferencedItemKey } from './relation-field-state-primitives';

/**
 * @internal
 */
export function useRelationFieldState(field: MaybeRefOrGetter<ResolvedMonospaceSingleRelationField>) {
	const formBlockContext = injectFormBlockContext(null);
	const computedField = computed(() => toValue(field));

	const fieldValue = computed<any>({
		get() {
			return formBlockContext?.state.value?.$getRaw(computedField.value.apiName);
		},
		set(value) {
			if (!formBlockContext?.state.value) return;
			formBlockContext.state.value[computedField.value.apiName] = value;
		},
	});

	const defaultHandlers: RelationHandlers = {
		handleCreate: noop,
		handleUndoCreate: noop,
		handleConnect: noop,
		handleUndoConnect: noop,
		handleDisconnect: noop,
		handleUndoDisconnect: noop,
		handleDelete: noop,
		handleUndoDelete: noop,
		handleUpdate: noop,
	};

	const specificHandlers = computed(() => {
		if (computedField.value.isList) {
			return createRelationFieldStateToManyHandler(toValue(field), fieldValue);
		}
		else if (formBlockContext?.isCreate.value) {
			return createRelationFieldStateToOneInCreateHandler(toValue(field), fieldValue, formBlockContext);
		}
		else {
			return createRelationFieldStateToOneInUpdateHandler(toValue(field), fieldValue, formBlockContext);
		}
	});

	const handlers = reactiveComputed(() => ({
		...defaultHandlers,
		...specificHandlers.value,
	}));

	const referencedItemKey = computed(() => {
		if (!formBlockContext) return null;

		const { thisSideConstrained } = getRelationConstraint(computedField.value);

		if (!thisSideConstrained && formBlockContext.itemKey.value) {
			// O2M: use stable primary key from form context
			return formBlockContext.itemKey.value;
		}

		// M2O or create mode: use live form state
		if (!formBlockContext.state.value) return null;

		return toReferencedItemKey(formBlockContext.state.value, computedField.value);
	});

	return {
		fieldValue: computed(() => fieldValue.value),
		referencedItemKey,
		handleCreate: (payload: AnyItem) => handlers.handleCreate(payload),
		handleUndoCreate: (payload: AnyItem) => handlers.handleUndoCreate(payload),
		handleConnect: (key: CompositePrimaryKey) => handlers.handleConnect(key),
		handleUndoConnect: (key: CompositePrimaryKey) => handlers.handleUndoConnect(key),
		handleDisconnect: (key: CompositePrimaryKey) => handlers.handleDisconnect(key),
		handleUndoDisconnect: (key: CompositePrimaryKey) => handlers.handleUndoDisconnect(key),
		handleDelete: (key: CompositePrimaryKey) => handlers.handleDelete(key),
		handleUndoDelete: (key: CompositePrimaryKey) => handlers.handleUndoDelete(key),
		handleUpdate: (originalKey: CompositePrimaryKey | null, payload: AnyItem) => handlers.handleUpdate(originalKey, payload),
	};
}
